import type {
  CasinoCompletedRaces,
  CasinoGameDeck,
  CasinoGameGrid,
  CasinoGameOfTheWeek,
  CasinoGameSlider,
  CasinoGameTrailer,
  CasinoJackpot,
  CasinoMosaic,
  CasinoMyGames,
  CasinoNewGames,
  CasinoPlayerGames,
  CasinoTopGames
} from '@ps/web-martech-cms-types';
import type { Dispatch } from 'redux';
import { first, share } from 'rxjs/operators';

import { select } from '@swf/core/store';
import type { EffectParams, WidgetEffectParams } from '@swf/core/store';
import { isVisibleByAB } from '@swf/core/utils/casino';

export const checkAuth = ({
  dispatch,
  rootState,
  actions
}: Omit<EffectParams, 'request' | 'addModel' | 'select' | 'store'>) => {
  if (!select.session.isAuthenticated(rootState)) {
    dispatch.modal.enqueue({ path: '/auth/login' });
    return actions.pipe(
      first(({ state }) => select.session.isAuthenticated(state)),
      share()
    );
  }
  return undefined;
};

// TODO remove from casino core model utils
export interface NavItem {
  text: string;
  url: string;
}

export const getActive = (pathname: string, items: NavItem[]): string[] =>
  items
    .map((item) => item.url)
    .filter((url) => (url ? pathname.endsWith(url) || pathname.endsWith(`${url}/`) : false))
    .sort((a, b) => b.length - a.length);

export const initialState = { games: [], subscriptions: {}, loading: false };

export const isServiceAuthOnly = (service: string) =>
  [
    'casino.games.recent.stream',
    'casino.games.recommended.stream',
    'casino.games.myfsb',
    'casino.games.playergames'
  ].includes(service);

type WidgetsWithTargeting =
  | CasinoCompletedRaces
  | CasinoGameDeck
  | CasinoGameGrid
  | CasinoGameOfTheWeek
  | CasinoGameSlider
  | CasinoGameTrailer
  | CasinoMosaic
  | CasinoMyGames
  | CasinoNewGames
  | CasinoPlayerGames
  | CasinoTopGames
  | CasinoJackpot;

export const areGamesVisibleByConfig = ({
  params,
  service,
  hasState
}: {
  params: WidgetEffectParams<WidgetsWithTargeting, Dispatch>;
  service: string;
  hasState: boolean;
}) => {
  const {
    dispatch,
    rootState,
    select,
    widget: { uid, targeting }
  } = params;
  const widgetDispatch = dispatch[uid];

  const authenticated = select.session.isClientAuthenticated(rootState);

  const isABVisible = isVisibleByAB(rootState, targeting);
  const isVisibleByServiceAndAuth =
    (isServiceAuthOnly(service) && authenticated) || !isServiceAuthOnly(service);

  if (!isABVisible && hasState) {
    widgetDispatch.clearState();
  }

  return isABVisible && isVisibleByServiceAndAuth;
};
